import { useCallback, useState } from 'react';
import { Timer } from 'lucide-react';
import { reconReportApi } from '@/api/reportCentre.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { exportToExcel, exportToCsvRows } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, fmtDate, fmtAmt, fmtAmtZero, EmptyState, Sparkline,
} from '@/components/analytics/AnalyticsKit';
import { Pill, TabBar, InfoBanner, Dash, errMessage } from './ReportCentreKit';
import type { PillTone } from './ReportCentreKit';
import type {
  SuspenseReportDto, SuspenseAccount, ClearingOutstandingDto, ClearingAccount,
} from '@/types/reportCentre.types';

const REPORT_KEY = 'SUSPENSE_CLEARING';
const DEFAULT_BUCKETS = '7,30,60,90';

/** Age-bucket chip tone: the older the bucket, the angrier the colour. */
function bucketTone(bucket: string, labels: string[]): PillTone {
  const idx = labels.indexOf(bucket);
  if (idx < 0) return 'grey';
  const pos = labels.length <= 1 ? 1 : idx / (labels.length - 1);
  if (pos >= 0.75) return 'red';
  if (pos >= 0.4) return 'amber';
  return 'green';
}

const CLEARING_TONES: Record<string, PillTone> = {
  CLEAR:       'green',
  OK:          'green',
  OUTSTANDING: 'amber',
};

// ── Export column sets ────────────────────────────────────────────────────────

const SUSPENSE_COLUMNS: ExcelColumn[] = [
  { key: 'Account',   header: 'Account',     width: 30 },
  { key: 'Journal',   header: 'Journal No',  width: 18 },
  { key: 'Date',      header: 'Posting Date', width: 14 },
  { key: 'Narration', header: 'Narration',   width: 36 },
  { key: 'Reference', header: 'Reference',   width: 20 },
  { key: 'Debit',     header: 'Debit',       width: 14, numFmt: '#,##0.00' },
  { key: 'Credit',    header: 'Credit',      width: 14, numFmt: '#,##0.00' },
  { key: 'Running',   header: 'Running Balance', width: 16, numFmt: '#,##0.00' },
  { key: 'AgeDays',   header: 'Age (days)',  width: 12 },
  { key: 'Bucket',    header: 'Age Bucket',  width: 14 },
];

function buildSuspenseRows(accounts: SuspenseAccount[]): Record<string, unknown>[] {
  return accounts.flatMap(a =>
    a.lines.map(l => ({
      Account:   `${a.accountCode} — ${a.accountName}`,
      Journal:   l.journalNumber,
      Date:      l.postingDate,
      Narration: l.narration ?? '',
      Reference: l.reference ?? '',
      Debit:     l.debit,
      Credit:    l.credit,
      Running:   l.runningBalance,
      AgeDays:   l.ageDays,
      Bucket:    l.ageBucket,
    })),
  );
}

const CLEARING_COLUMNS: ExcelColumn[] = [
  { key: 'Role',     header: 'Role',            width: 22 },
  { key: 'Account',  header: 'Account',         width: 30 },
  { key: 'Net',      header: 'Net Outstanding', width: 16, numFmt: '#,##0.00' },
  { key: 'LastMove', header: 'Last Movement',   width: 16 },
  { key: 'DaysIdle', header: 'Days Since',      width: 12 },
  { key: 'Status',   header: 'Status',          width: 14 },
];

function buildClearingRows(accounts: ClearingAccount[]): Record<string, unknown>[] {
  return accounts.map(a => ({
    Role:     a.role,
    Account:  `${a.accountCode} — ${a.accountName}`,
    Net:      a.netOutstanding,
    LastMove: a.lastMovementDate ?? '',
    DaysIdle: a.daysSinceLastMovement ?? '',
    Status:   a.status,
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

type Tab = 'suspense' | 'clearing';

export const SuspenseClearingPage = () => {
  const { hasPermission } = usePermission();

  const [tab, setTab] = useState<Tab>('suspense');

  // Suspense tab state
  const [susAsAt, setSusAsAt] = useState(today());
  const [bucketDays, setBucketDays] = useState(DEFAULT_BUCKETS);
  const [susData, setSusData] = useState<SuspenseReportDto | null>(null);
  const [susLoading, setSusLoading] = useState(false);
  const [susError, setSusError] = useState<string | null>(null);

  // Clearing tab state
  const [clrAsAt, setClrAsAt] = useState(today());
  const [clrData, setClrData] = useState<ClearingOutstandingDto | null>(null);
  const [clrLoading, setClrLoading] = useState(false);
  const [clrError, setClrError] = useState<string | null>(null);

  const runSuspense = useCallback(async () => {
    setSusLoading(true);
    setSusError(null);
    try {
      const res = await reconReportApi.getSuspense({ asAt: susAsAt, bucketDays });
      setSusData(res.data?.data ?? null);
    } catch (e: unknown) {
      setSusError(errMessage(e));
    } finally {
      setSusLoading(false);
    }
  }, [susAsAt, bucketDays]);

  const runClearing = useCallback(async () => {
    setClrLoading(true);
    setClrError(null);
    try {
      const res = await reconReportApi.getClearingOutstanding({ asAt: clrAsAt });
      setClrData(res.data?.data ?? null);
    } catch (e: unknown) {
      setClrError(errMessage(e));
    } finally {
      setClrLoading(false);
    }
  }, [clrAsAt]);

  if (!hasPermission(Permissions.RPT_RECON)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const validBuckets = /^\s*\d+\s*(,\s*\d+\s*)*$/.test(bucketDays);
  const hasData = tab === 'suspense' ? !!susData : !!clrData;
  const rowCount = tab === 'suspense'
    ? (susData?.accounts ?? []).reduce((n, a) => n + a.lines.length, 0)
    : (clrData?.accounts ?? []).length;

  const handleExcel = () => {
    if (tab === 'suspense') {
      if (!susData) return;
      exportToExcel({
        sheetName: 'Suspense Ageing',
        columns: SUSPENSE_COLUMNS,
        rows: buildSuspenseRows(susData.accounts),
        title: 'Suspense Account Ageing',
        subtitle: `${susData.companyName} · As at ${susData.asAt}`,
      }, `SuspenseAgeing_${susData.asAt}`);
    } else {
      if (!clrData) return;
      exportToExcel({
        sheetName: 'Clearing Outstanding',
        columns: CLEARING_COLUMNS,
        rows: buildClearingRows(clrData.accounts),
        title: 'Clearing Accounts — Net Outstanding',
        subtitle: `${clrData.companyName} · As at ${clrData.asAt}`,
      }, `ClearingOutstanding_${clrData.asAt}`);
    }
  };

  const handleCsv = () => {
    if (tab === 'suspense') {
      if (!susData) return;
      exportToCsvRows(SUSPENSE_COLUMNS, buildSuspenseRows(susData.accounts), `SuspenseAgeing_${susData.asAt}`);
    } else {
      if (!clrData) return;
      exportToCsvRows(CLEARING_COLUMNS, buildClearingRows(clrData.accounts), `ClearingOutstanding_${clrData.asAt}`);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Timer size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Suspense &amp; Clearing Accounts</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onCsvExport={handleCsv}
          hasData={hasData}
          reportKey={REPORT_KEY}
          rowCount={rowCount}
          filterSummary={tab === 'suspense' ? `asAt=${susAsAt} buckets=${bucketDays}` : `asAt=${clrAsAt}`}
        />
      </div>

      <TabBar
        tabs={[
          { key: 'suspense', label: 'Suspense Ageing' },
          { key: 'clearing', label: 'Clearing Outstanding' },
        ]}
        active={tab}
        onChange={k => setTab(k as Tab)}
      />

      {/* ── Suspense tab ─────────────────────────────────────────────────────── */}
      {tab === 'suspense' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap gap-3">
                <div className="w-44">
                  <label className={LABEL_CLS}>As At *</label>
                  <input type="date" value={susAsAt} onChange={e => setSusAsAt(e.target.value)} className={INPUT_CLS} />
                </div>
                <div className="w-52">
                  <label className={LABEL_CLS}>Age Buckets (days, csv)</label>
                  <input
                    type="text"
                    value={bucketDays}
                    onChange={e => setBucketDays(e.target.value)}
                    placeholder={DEFAULT_BUCKETS}
                    className={cn(INPUT_CLS, !validBuckets && 'border-red-400 dark:border-red-600')}
                  />
                  {!validBuckets && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">Comma-separated day numbers, e.g. 7,30,60,90</p>
                  )}
                </div>
              </div>
              <RunReportButton
                onClick={() => { void runSuspense(); }}
                isLoading={susLoading}
                disabled={!susAsAt || !validBuckets}
              />
            </div>
          </div>

          {susError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {susError}
            </div>
          )}

          {susLoading && !susData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!susData && !susLoading && (
            <EmptyState message='Pick an "As At" date and click "Run Report" to age the suspense account balances.' />
          )}

          {susData && (
            <div className="flex flex-col gap-4">
              <div className={cn(CARD_CLS, 'px-4 py-3')}>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{susData.companyName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Suspense Ageing — as at {fmtDate(susData.asAt)} · Generated {fmtDate(susData.generatedAt)}
                </div>
              </div>

              {susData.status === 'NOT_CONFIGURED' ? (
                <InfoBanner>
                  <span className="font-medium">No suspense accounts are configured.</span>{' '}
                  {susData.note ?? 'Map a SUSPENSE role in the account role configuration to activate this report.'}
                </InfoBanner>
              ) : susData.accounts.length === 0 ? (
                <InfoBanner>No open suspense balances as at this date.{susData.note ? ` ${susData.note}` : ''}</InfoBanner>
              ) : (
                <>
                  {susData.note && <InfoBanner>{susData.note}</InfoBanner>}

                  {/* Grand totals by bucket */}
                  <div className="flex flex-wrap items-center gap-2">
                    {susData.totalsByBucket.map(t => (
                      <span
                        key={t.bucket}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                      >
                        {t.bucket}
                        <span className="font-bold tabular-nums">{fmtAmtZero(t.net)}</span>
                      </span>
                    ))}
                  </div>

                  {/* Per-account cards */}
                  {susData.accounts.map(acc => (
                    <div key={acc.accountId} className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
                        <div>
                          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                            <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-1.5">{acc.accountCode}</span>
                            {acc.accountName}
                          </h2>
                          <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                            Net balance {fmtAmtZero(acc.netBalance)}
                            {acc.truncatedLineCount > 0 &&
                              ` · ${acc.truncatedLineCount.toLocaleString('en-US')} oldest lines omitted (line cap reached)`}
                          </p>
                        </div>
                        {/* Bucket totals row */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {acc.bucketTotals.map(t => (
                            <span
                              key={t.bucket}
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium tabular-nums',
                                'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
                              )}
                            >
                              {t.bucket}: {fmtAmtZero(t.net)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="overflow-auto">
                        <table className="w-full text-sm border-collapse min-w-[1100px]">
                          <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                              <th className={TH_CLS}>Journal No</th>
                              <th className={cn(TH_CLS, 'w-28')}>Posting Date</th>
                              <th className={TH_CLS}>Narration</th>
                              <th className={TH_CLS}>Reference</th>
                              <th className={TH_R_CLS}>Debit</th>
                              <th className={TH_R_CLS}>Credit</th>
                              <th className={TH_R_CLS}>Running</th>
                              <th className={TH_R_CLS}>Age</th>
                              <th className={cn(TH_CLS, 'w-24')}>Bucket</th>
                            </tr>
                          </thead>
                          <tbody>
                            {acc.lines.length === 0 ? (
                              <tr>
                                <td colSpan={9} className="px-3 py-6 text-center text-sm text-gray-400 dark:text-gray-600">
                                  No open lines for this account.
                                </td>
                              </tr>
                            ) : acc.lines.map((l, i) => (
                              <tr key={`${l.journalNumber}-${i}`} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100 whitespace-nowrap">{l.journalNumber}</td>
                                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{fmtDate(l.postingDate)}</td>
                                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{l.narration ?? <Dash />}</td>
                                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{l.reference ?? <Dash />}</td>
                                <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(l.debit)}</td>
                                <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(l.credit)}</td>
                                <td className={cn(AMT_CLS, 'font-medium text-gray-900 dark:text-gray-100')}>{fmtAmtZero(l.runningBalance)}</td>
                                <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{l.ageDays}d</td>
                                <td className="px-3 py-2">
                                  <Pill label={l.ageBucket} tone={bucketTone(l.ageBucket, susData.bucketLabels)} />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Clearing tab ─────────────────────────────────────────────────────── */}
      {tab === 'clearing' && (
        <>
          <div className={cn(CARD_CLS, 'p-4')}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="w-44">
                <label className={LABEL_CLS}>As At *</label>
                <input type="date" value={clrAsAt} onChange={e => setClrAsAt(e.target.value)} className={INPUT_CLS} />
              </div>
              <RunReportButton onClick={() => { void runClearing(); }} isLoading={clrLoading} disabled={!clrAsAt} />
            </div>
          </div>

          {clrError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {clrError}
            </div>
          )}

          {clrLoading && !clrData && (
            <div className="flex-1 flex items-center justify-center"><Spinner size="lg" /></div>
          )}

          {!clrData && !clrLoading && (
            <EmptyState message='Pick an "As At" date and click "Run Report" to see the clearing accounts still carrying a net balance.' />
          )}

          {clrData && (
            <div className="flex flex-col gap-4">
              <div className={cn(CARD_CLS, 'px-4 py-3')}>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">{clrData.companyName}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Clearing Outstanding — as at {fmtDate(clrData.asAt)} · Generated {fmtDate(clrData.generatedAt)}
                </div>
              </div>

              {clrData.status === 'NOT_CONFIGURED' ? (
                <InfoBanner>
                  <span className="font-medium">No clearing accounts are configured.</span>{' '}
                  {clrData.note ?? 'Map clearing roles in the account role configuration to activate this report.'}
                </InfoBanner>
              ) : (
                <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
                  {clrData.note && <div className="p-4 pb-0"><InfoBanner>{clrData.note}</InfoBanner></div>}
                  <div className="overflow-auto">
                    <table className="w-full text-sm border-collapse min-w-[1000px]">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className={TH_CLS}>Role</th>
                          <th className={TH_CLS}>Account</th>
                          <th className={TH_R_CLS}>Net Outstanding</th>
                          <th className={cn(TH_CLS, 'w-32')}>Last Movement</th>
                          <th className={TH_R_CLS}>Days Since</th>
                          <th className={cn(TH_CLS, 'w-28')}>6-Month Net</th>
                          <th className={cn(TH_CLS, 'w-28')}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clrData.accounts.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                              No clearing accounts to show.
                            </td>
                          </tr>
                        ) : clrData.accounts.map(a => (
                          <tr key={a.accountId} className="border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">{a.role}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                              <span className="font-mono text-xs text-gray-500 dark:text-gray-400 mr-1.5">{a.accountCode}</span>
                              {a.accountName}
                            </td>
                            <td className={cn(
                              AMT_CLS,
                              Math.abs(a.netOutstanding) >= 0.01
                                ? 'font-bold text-gray-900 dark:text-white'
                                : 'text-gray-400 dark:text-gray-600',
                            )}>
                              {fmtAmtZero(a.netOutstanding)}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              {a.lastMovementDate ? fmtDate(a.lastMovementDate) : <Dash />}
                            </td>
                            <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                              {a.daysSinceLastMovement === null ? <Dash /> : a.daysSinceLastMovement}
                            </td>
                            <td className="px-3 py-2">
                              <Sparkline values={a.monthlyNet.map(m => m.net)} />
                            </td>
                            <td className="px-3 py-2">
                              <Pill label={a.status} tone={CLEARING_TONES[a.status] ?? 'grey'} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
